import React from "react";
import { cn } from "../../lib/cn";

type Variant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "success"
  | "warning";

type Size = "xs" | "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-accent text-white border-transparent hover:bg-accent-hover active:bg-accent-hover shadow-panel",
  secondary:
    "bg-surface text-ink border-line hover:bg-surface-hover active:bg-surface-active",
  ghost:
    "bg-transparent text-ink-muted border-transparent hover:bg-surface-hover hover:text-ink active:bg-surface-active",
  danger:
    "bg-danger-soft text-danger border-transparent hover:bg-danger hover:text-white",
  success: "bg-success text-white border-transparent hover:brightness-110",
  warning: "bg-warning-soft text-warning border-transparent hover:brightness-95",
};

const SIZES: Record<Size, string> = {
  xs: "h-6 px-1.5 text-xs gap-1 rounded-md",
  sm: "h-8 px-2.5 text-sm gap-1.5 rounded-lg",
  md: "h-9 px-3.5 text-sm gap-2 rounded-lg",
};

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  /** Renders a square button sized for a single icon. */
  iconOnly?: boolean;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "secondary",
      size = "sm",
      iconOnly = false,
      className,
      type = "button",
      children,
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex items-center justify-center border font-medium whitespace-nowrap",
          "transition-[background-color,color,border-color,transform,opacity]",
          "duration-(--duration-instant) ease-(--ease-standard)",
          "active:scale-[0.97]",
          "disabled:pointer-events-none disabled:opacity-45",
          SIZES[size],
          iconOnly && (size === "xs" ? "w-6 px-0" : size === "sm" ? "w-8 px-0" : "w-9 px-0"),
          VARIANTS[variant],
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

export default Button;
